import { INTERACTION_CONFIG } from '@common/config';
import { getPrimaryInteraction } from '@common/data/Interactions';
import { findClosestInteractable } from '@common/utils/InteractionUtils';
import { Container, Text } from 'pixi.js';

import { ClientWorld } from '../ClientWorld';
import { ClientRegistry } from '../components';
import { ClientServiceContainer } from '../services/ClientServiceContainer';
import {
  ClientFilteredComponentSystem,
  FilteredClientComponentSystemUpdateEntityArgs,
} from './ClientBase';

interface HintTextData {
  text: Text;
}

// Module-level cache for text objects
const hintTexts = new Map<number, HintTextData>();

// Z-Index for interaction hints (always on top)
const HINT_Z_INDEX = 1000;
// Padding above the entity or bounds
const HINT_PADDING_TOP = 10;
// Counter-scale for text rendering (zoomed in camera)
const TEXT_SCALE_ZOOM = 6;
// Font size for hint text
const FONT_SIZE = 20;
// Stroke width for hint text
const STROKE_WIDTH = 3;

/**
 * System that renders visual hints (e.g., "Chat", "Open Door") above interactable entities.
 *
 * Checks specifically for the closest interactable entity within range and renders a single hint.
 * Uses exact bounds checking logic that mirrors the server's PlayerInputIntentSystem to ensure fidelity.
 */
export class InteractionHintRenderSystem extends ClientFilteredComponentSystem {
  public componentClasses: (keyof ClientRegistry)[] = ['InteractionHint', 'Transform'];

  // Interaction rendering logic: detects the closest interactable entity to determine hint visibility.

  private hintsContainer: Container | null = null;

  // Track the entity that is currently "closest" and should show a hint
  private closestEntityId: number | null = null;

  constructor(clientServices: ClientServiceContainer) {
    super(clientServices);
  }

  setHintsContainer(container: Container) {
    this.hintsContainer = container;
  }

  /**
   * Override run to perform "closest entity" check *before* processing individual entities.
   * This ensures we only render the hint for the single valid interaction target.
   */
  override run(deltaTime: number): void {
    if (!this.hintsContainer) return;

    // 1. Find local player
    const playerEntityId = this.clientServices.localPlayer.entityId;
    if (playerEntityId === undefined) {
      this.closestEntityId = null;
      super.run(deltaTime);
      return;
    }

    // 2. Find closest interactable using utility
    this.closestEntityId = findClosestInteractable(
      this.getWorld(),
      playerEntityId,
      INTERACTION_CONFIG.HINT_RADIUS,
      this.componentClasses,
      this.excludedComponentClasses,
    );

    // 3. Run standard loop which calls updateEntity
    super.run(deltaTime);
  }

  updateEntity({ world, entityId }: FilteredClientComponentSystemUpdateEntityArgs): void {
    if (!this.hintsContainer) return;

    const hintData = world.getComponent(entityId, 'InteractionHint')!;
    const transform = world.getComponent(entityId, 'Transform')!;
    const collider = world.getComponent(entityId, 'Collider'); // Optional

    let textData = hintTexts.get(entityId);

    const shouldShow = entityId === this.closestEntityId;

    if (shouldShow) {
      const playerEntityId = this.clientServices.localPlayer.entityId;
      if (playerEntityId === undefined) return;

      const interaction = getPrimaryInteraction(world, playerEntityId, entityId);
      const displayHintText = interaction ? interaction.label : hintData.text;

      // Color based on whether we are in execution range
      const distance = this.calculateDistanceToEntity(world, playerEntityId, entityId);
      const isAggroed = world.hasComponent(entityId, 'Aggro');
      const canExecute = distance <= INTERACTION_CONFIG.EXECUTE_RADIUS && !isAggroed;

      let textColor = 0xaaaaaa; // Faded if just in hint range
      if (isAggroed) {
        textColor = 0x666666; // Grey out if aggro'd
      } else if (canExecute) {
        textColor = 0xffff00; // Yellow if in execution range
      }

      // Create text if missing
      if (!textData) {
        const text = new Text({
          text: displayHintText,
          style: {
            fontFamily: 'monospace',
            fontSize: FONT_SIZE,
            fill: textColor,
            stroke: { color: 0x000000, width: STROKE_WIDTH },
            align: 'center',
            dropShadow: {
              color: '#000000',
              alpha: 0.8,
              blur: 2,
              distance: 1,
            },
          },
        });
        text.anchor.set(0.5, 1); // Anchor bottom-center
        text.zIndex = HINT_Z_INDEX;
        this.hintsContainer.addChild(text);
        textData = { text };
        hintTexts.set(entityId, textData);

        // Register cleanup callback to destroy PixiJS objects when entity is destroyed
        world.registerOnEntityDestroyed(entityId, (id) => this.cleanupEntity(id));
      }

      // Sync hint text and color if they changed
      if (textData.text.text !== displayHintText) {
        textData.text.text = displayHintText;
      }
      if (textData.text.style.fill !== textColor) {
        textData.text.style.fill = textColor;
      }

      // Calculate Target Y ("Top" of the object)
      let topY = transform.y;

      if (collider) {
        topY = transform.y + collider.offsetY;
      }

      // Interpolate X/Y
      const visPos = world.getComponent(entityId, 'VisualPosition');
      const finalX = visPos ? visPos.x : transform.x;
      // Adjust finalY based on the offset from transform.y
      const offsetY = topY - transform.y;

      // Padding above top
      const renderY = (visPos ? visPos.y : transform.y) + offsetY - HINT_PADDING_TOP;

      // Counter-scale
      textData.text.scale.set(1 / TEXT_SCALE_ZOOM);

      // Center X logic
      let centerXOffset = 0;
      if (collider) {
        centerXOffset = collider.offsetX + collider.width / 2;
      }

      const renderX = finalX + centerXOffset;

      textData.text.position.set(renderX, renderY);
      textData.text.visible = true;
    } else {
      // Should NOT show
      if (textData) {
        textData.text.visible = false;
      }
    }
  }

  private calculateDistanceToEntity(
    world: ClientWorld,
    playerEntityId: number,
    targetEntityId: number,
  ): number {
    const playerTransform = world.getComponent(playerEntityId, 'Transform');
    const playerCollider = world.getComponent(playerEntityId, 'Collider');
    const targetTransform = world.getComponent(targetEntityId, 'Transform');
    const targetCollider = world.getComponent(targetEntityId, 'Collider');

    if (!playerTransform || !playerCollider || !targetTransform) return Infinity;

    const pL = playerTransform.x + playerCollider.offsetX;
    const pT = playerTransform.y + playerCollider.offsetY;
    const pR = pL + playerCollider.width;
    const pB = pT + playerCollider.height;

    let tL, tT, tR, tB;
    if (targetCollider) {
      tL = targetTransform.x + targetCollider.offsetX;
      tT = targetTransform.y + targetCollider.offsetY;
      tR = tL + targetCollider.width;
      tB = tT + targetCollider.height;
    } else {
      tL = tR = targetTransform.x;
      tT = tB = targetTransform.y;
    }

    const distX = Math.max(tL - pR, pL - tR, 0);
    const distY = Math.max(tT - pB, pT - tB, 0);
    return Math.sqrt(distX * distX + distY * distY);
  }

  private cleanupEntity(entityId: number) {
    const textData = hintTexts.get(entityId);
    if (textData) {
      textData.text.destroy();
      hintTexts.delete(entityId);
    }
  }
}
